var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { EPricingRuleType, EPricingRuleTypeInt } from './booking-calculation-price-rule-model';
import { getTicketPricingRuleBracket, getTicketTier, getEarlyBirdPricingRuleBracket } from './booking-calculation-price-rule-util';
import { createBlankBookingAmounts, modifierCostCalculation, roundToTwo } from './booking-calculation-util';
var BookingCalculationService = /** @class */function () {
  function BookingCalculationService() {
    var _this = this;
    this.getItemCostWithTaxOverride = function (item, itemCost, data) {
      if (!data.applyNoTax || !data.costSettings.productPricesIncludeTax) {
        return itemCost;
      }
      return Math.max(0, itemCost - _this.getTaxAmount(itemCost, item, data, {
        includeInclusiveTax: true,
        ignoreApplyNoTax: true
      }));
    };
  }
  BookingCalculationService.prototype.getAmounts = function (data, options) {
    if (!data.items.length) {
      return createBlankBookingAmounts();
    }
    var subTotal = this.getSubTotal(data, {
      ignoreDiscount: true,
      allowNegative: options.allowNegative
    });
    var subTotalTax = roundToTwo(this.getTax(data, __assign(__assign({}, options), {
      includeInclusiveTax: true
    })));
    var transactionFees = this.getTransactionFee(data, options);
    var creditCardFees = this.getCreditCardFee(data, options);
    var fees = roundToTwo(this.getTotalFees(data, options));
    var feesTax = roundToTwo(this.getFeeTax(data, options));
    var tax = roundToTwo(subTotalTax + feesTax);
    var taxExclusive = roundToTwo(this.getTax(data, __assign(__assign({}, options), {
      includeInclusiveTax: false
    })));
    var giftCardValue = this.getGiftCardValue(data.giftCardResult);
    var discount = this.getDiscount(data.discountResult);
    var postage = this.calcPostage(data.items);
    var totalCost = this.getTotal(data, options);
    var totalExcCardFees = this.getTotalExcludingCardFees(data, options);
    var totalIgnoringDeposit = this.getTotal(data, __assign(__assign({}, options), {
      considerDeposit: false
    }));
    var deposit = this.getSubTotal(data, __assign(__assign({}, options), {
      ignoreDiscount: false
    }));
    var depositIncFees = this.getTotal(data, {
      considerDeposit: true
    });
    var total = roundToTwo(totalCost - giftCardValue);
    var giftCard = giftCardValue;
    if (giftCardValue > 0 && giftCardValue > totalCost) {
      total = 0;
      giftCard = totalCost;
    }
    var paymentAmountIncCardFees = data.paymentAmount === null || data.paymentAmount === undefined ? total : data.paymentAmount + this.getCreditCardFee(data, options);
    return {
      creditCardFees: creditCardFees,
      deposit: deposit,
      depositIncFees: depositIncFees,
      discount: discount,
      fees: fees,
      feesTax: feesTax,
      giftCard: giftCard,
      paymentAmountIncCardFees: paymentAmountIncCardFees,
      postage: postage,
      subTotal: subTotal,
      subTotalTax: subTotalTax,
      tax: tax,
      taxExclusive: taxExclusive,
      total: total,
      totalExcCardFees: totalExcCardFees,
      totalIgnoringDeposit: totalIgnoringDeposit,
      transactionFees: transactionFees
    };
  };
  BookingCalculationService.prototype.getTicketCost = function (item) {
    var _a;
    if (!!item.sessionDiscount) {
      return this.getConsecutiveSessionTicketCost(item.cost, item.sessionDiscount);
    } else if ((_a = item.modifiers) === null || _a === void 0 ? void 0 : _a.length) {
      return item.cost + modifierCostCalculation(item);
    } else if (item.pricingRule) {
      return this.calculatePricingRulePrice(item);
    }
    return item.cost;
  };
  BookingCalculationService.prototype.getTicketTax = function (item, costSettings) {
    var ticketCost = this.getTicketCost(item);
    return this.getTaxAmount(ticketCost, item, {
      items: [],
      costSettings: costSettings
    }, {
      includeInclusiveTax: true
    });
  };
  BookingCalculationService.prototype.getItemTax = function (item, costSettings) {
    return this.getTax({
      items: [item],
      costSettings: costSettings
    }, {
      includeInclusiveTax: true
    });
  };
  BookingCalculationService.prototype.isItemTaxInclusive = function (item, costSettings) {
    return item.isTaxInclusive || item.isTaxInclusive !== false && costSettings.productPricesIncludeTax;
  };
  BookingCalculationService.prototype.getConsecutiveSessionTicketCost = function (ticketCost, sessionDiscount) {
    var costBeforeDiscount = ticketCost * sessionDiscount.numberOfSessions;
    if (!!sessionDiscount.amountOff) {
      costBeforeDiscount -= sessionDiscount.amountOff;
    } else if (!!sessionDiscount.percentOff) {
      costBeforeDiscount -= costBeforeDiscount * sessionDiscount.percentOff / 100;
    }
    return costBeforeDiscount;
  };
  BookingCalculationService.prototype.getTaxAmount = function (amount, item, data, options) {
    if (data.applyNoTax && !options.ignoreApplyNoTax) {
      return 0;
    }
    var taxableAmount = options.considerDeposit && item.depositAmount ? item.depositAmount : amount;
    if (this.isItemTaxInclusive(item, data.costSettings)) {
      if (options.includeInclusiveTax) {
        return roundToTwo(taxableAmount - taxableAmount / (1 + item.tax / 100));
      }
    } else {
      return roundToTwo(taxableAmount * item.tax / 100);
    }
    return 0;
  };
  /* Tax on fees is always inclusive - it does not impact the total amount */
  BookingCalculationService.prototype.getFeeTax = function (data, options) {
    if (!data.costSettings.feeTaxPercent || data.applyNoTax && !options.ignoreApplyNoTax) {
      return 0;
    }
    var fees = this.getTotalFees(data, options);
    return roundToTwo(fees - fees / (1 + data.costSettings.feeTaxPercent / 100));
  };
  BookingCalculationService.prototype.getTotal = function (data, options) {
    var subTotal = this.getSubTotal(data, options);
    var tax = this.getTax(data, options);
    var fees = this.getTotalFees(data, options);
    //round because 23 + 2.24 = 25.240000000000002
    return roundToTwo(subTotal + tax + fees + (data.tip || 0) - (data.paid || 0));
  };
  BookingCalculationService.prototype.getTotalExcludingCardFees = function (data, options) {
    var subTotal = this.getSubTotal(data, options);
    var tax = this.getTax(data, options);
    var fees = this.getTransactionFee(data, options);
    return roundToTwo(subTotal + tax + fees + (data.tip || 0) - (data.paid || 0));
  };
  BookingCalculationService.prototype.calcPostage = function (items) {
    if (items.length === 0) {
      return 0;
    }
    var onlyGiftCards = items.filter(function (item) {
      var _a;
      return !!((_a = item.giftCardPostageItems) === null || _a === void 0 ? void 0 : _a.length);
    });
    if (!onlyGiftCards.length) {
      return 0;
    }
    return onlyGiftCards.map(function (item) {
      if (!item.giftCardPostageItems) {
        return 0;
      }
      var sumPostage = item.giftCardPostageItems.reduce(function (acc, postage) {
        return acc + postage.price;
      }, 0);
      return sumPostage;
    }).reduce(function (acc, currentValue) {
      return acc + currentValue;
    }, 0);
  };
  BookingCalculationService.prototype.getSubTotal = function (data, options) {
    var _this = this;
    if (data.items.length === 0) {
      return 0;
    }
    var itemsToSubTotal = options.considerDeposit ? this.getItemsConsideringFixedDeposit(data.items) : data.items;
    var subTotal = itemsToSubTotal.map(function (item) {
      var itemCost = _this.getItemCost(item, options, data);
      return options.considerDeposit && item.depositAmount ? item.depositAmount : itemCost;
    }).reduce(function (total, currentValue) {
      return total + currentValue;
    }, 0) + this.calcPostage(itemsToSubTotal);
    if (!options.ignoreDiscount) {
      var discount = this.getDiscount(data.discountResult);
      subTotal -= discount;
      if (!options.allowNegative && subTotal < 0) {
        subTotal = 0;
      }
    }
    return roundToTwo(subTotal);
  };
  BookingCalculationService.prototype.getItemCost = function (item, options, data) {
    var ticketCost = this.getTicketCost(item);
    var itemCost = ticketCost * item.quantity;
    var itemCostConsideringDeposit = options.considerDeposit && !!item.costPercentageRequired ? itemCost * (item.costPercentageRequired / 100) : itemCost;
    return this.getItemCostWithTaxOverride(item, itemCostConsideringDeposit, data);
  };
  BookingCalculationService.prototype.getItemsConsideringFixedDeposit = function (items) {
    var result = [];
    items.forEach(function (item) {
      if (!item.depositAmount || !result.some(function (i) {
        return i.parentProductId === item.parentProductId;
      })) {
        result.push(item);
      }
    });
    return result;
  };
  BookingCalculationService.prototype.getTax = function (data, options) {
    var _this = this;
    var _a;
    if (!!((_a = data.discountResult) === null || _a === void 0 ? void 0 : _a.length)) {
      if (options.includeInclusiveTax) {
        return data.discountResult.reduce(function (sum, current) {
          return sum + current.tax;
        }, 0);
      }
      return data.discountResult.reduce(function (sum, current) {
        return sum + current.exclusiveTax;
      }, 0);
    }
    var tax = 0;
    var taxableItems = options.considerDeposit ? this.getItemsConsideringFixedDeposit(data.items) : data.items;
    taxableItems.forEach(function (item) {
      // NOTE: due to decimal precision issues we multiply by 100 then divide at the end, examples below
      // 5.38875 + 2.09625 = 7.484999999999999
      // ((5.38875 * 100) + (2.09625 * 100)) / 100 = 7.485
      var itemCost = _this.getItemCost(item, options, data);
      tax += _this.getTaxAmount(itemCost, item, data, options) * 100;
    });
    return tax <= 0 ? 0 : roundToTwo(tax / 100);
  };
  BookingCalculationService.prototype.getTotalFees = function (data, options) {
    var transactionFee = this.getTransactionFee(data, options);
    var creditCardFee = this.getCreditCardFee(data, options);
    return transactionFee + creditCardFee;
  };
  /** Transaction fees are calculated pre-deposit and post-discount */
  BookingCalculationService.prototype.getTransactionFee = function (data, options) {
    if (options.guestPayFees === false || this.isFree(data)) {
      return 0;
    }
    var result = 0;
    var totalFeeableAmount = this.getSubTotal(data, {}) + this.getTax(data, {});
    if (totalFeeableAmount > 0) {
      if (data.costSettings.transactionFeePercent) {
        result = totalFeeableAmount * data.costSettings.transactionFeePercent / 100;
      }
      if (data.costSettings.feePerTransaction) {
        result += data.costSettings.feePerTransaction;
      }
      result += data.items.reduce(function (total, item) {
        return total + (item.perProductBookingFee || 0) * item.quantity;
      }, 0);
    }
    return roundToTwo(result);
  };
  /** Credit card fees are based entirely on what the final cost/payment is. */
  BookingCalculationService.prototype.getCreditCardFee = function (data, options) {
    if (!data.costSettings.creditCardFeePercent || options.guestPayFees === false || this.isFree(data)) {
      return 0;
    }
    var totalFeeableAmount = data.paymentAmount === null || data.paymentAmount === undefined ? this.getSubTotal(data, options) + this.getTax(data, options) + this.getTransactionFee(data, options) - this.getGiftCardValue(data.giftCardResult) - (data.paid || 0) : data.paymentAmount;
    return roundToTwo(totalFeeableAmount <= 0 ? 0 : totalFeeableAmount * data.costSettings.creditCardFeePercent / 100);
  };
  BookingCalculationService.prototype.getDiscount = function (result) {
    if (!result || result.length === 0) {
      return 0;
    }
    var discountList = result.map(function (disc) {
      return disc.discount;
    });
    return discountList.reduce(function (sum, current) {
      return sum + current;
    }, 0);
  };
  BookingCalculationService.prototype.getGiftCardValue = function (giftCard) {
    if (!giftCard || giftCard.length === 0) {
      return 0;
    }
    var deductList = giftCard.map(function (disc) {
      return disc.amount;
    });
    return deductList.reduce(function (sum, current) {
      return sum + current;
    }, 0);
  };
  BookingCalculationService.prototype.isFree = function (data) {
    var _a, _b;
    var isDiscountFree = ((_a = data.discountResult) === null || _a === void 0 ? void 0 : _a.some(function (dResult) {
      return dResult.isFree;
    })) || false;
    var isGiftCardFree = ((_b = data.giftCardResult) === null || _b === void 0 ? void 0 : _b.some(function (gResult) {
      return gResult.isFree;
    })) || false;
    return isDiscountFree || isGiftCardFree;
  };
  BookingCalculationService.prototype.calculatePricingRulePrice = function (item) {
    var _a;
    switch ((_a = item.pricingRule) === null || _a === void 0 ? void 0 : _a.type) {
      case EPricingRuleType.quantity:
      case EPricingRuleTypeInt.quantity:
        {
          var bracket = getTicketPricingRuleBracket(item);
          return bracket ? bracket.price : item.cost;
        }
      case EPricingRuleType.time:
      case EPricingRuleTypeInt.time:
        {
          if (item.pricingRule.timeTiers && item.pricingRule.timeRule && item.bookingDate) {
            var tier = getTicketTier(item.pricingRule.timeTiers, item.pricingRule.timeRule, item.bookingDate, item.sessionStart);
            return tier ? tier.price : item.cost;
          }
          return item.cost;
        }
      case EPricingRuleType.earlyBird:
      case EPricingRuleTypeInt.earlyBird:
        {
          if (item.pricingRule.earlyBirdBrackets && item.bookingDate) {
            var bracket = getEarlyBirdPricingRuleBracket(item.bookingDate, item.pricingRule.earlyBirdBrackets, item.venueTime, item.bookingCreatedDate);
            return bracket ? bracket.price : item.cost;
          }
          return item.cost;
        }
      default:
        return item.cost;
    }
  };
  return BookingCalculationService;
}();
export { BookingCalculationService };